import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import type { UserRoom } from "@tuit/shared-types";
import { fetchUserRooms, createUserRoom, deleteUserRoom } from "../api.js";
import { useBillingStatus } from "../hooks/useBillingStatus.js";
import styles from "../styles/pages/MyRoomsPage.module.css";

export function MyRoomsPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<UserRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { status: billing } = useBillingStatus();

  useEffect(() => {
    if (isLoaded && !isSignedIn) navigate({ to: "/" });
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!isSignedIn) return;
    fetchUserRooms()
      .then(setRooms)
      .finally(() => setLoading(false));
  }, [isSignedIn]);

  function deriveSlug(v: string): string {
    return v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setCreating(true);
    try {
      const room = await createUserRoom({ title, slug, isPublic });
      setRooms((prev) => [...prev, room]);
      setTitle("");
      setSlug("");
      setIsPublic(false);
      setShowForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create room");
    } finally {
      setCreating(false);
    }
  }

  const roomLimit = billing?.capabilities.plan === "pro" ? Infinity : billing?.capabilities.plan === "plus" ? 50 : 3;
  const atLimit = rooms.length >= roomLimit;

  async function handleDelete(room: UserRoom) {
    if (!confirm(`Delete "${room.title}"? This cannot be undone.`)) return;
    setDeletingId(room.id);
    try {
      await deleteUserRoom(room.id);
      setRooms((prev) => prev.filter((r) => r.id !== room.id));
    } finally {
      setDeletingId(null);
    }
  }

  if (!isLoaded || !isSignedIn) return null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/" className={styles.back}>← Feed</Link>
        <h1 className={styles.title}>My Rooms</h1>
      </header>

      <div className={styles.body}>
        {loading ? (
          <p className={styles.hint}>Loading…</p>
        ) : rooms.length === 0 && !showForm ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyTitle}>No rooms yet</p>
            <p className={styles.emptyHint}>Create a room to organise your feeds into a personalised view.</p>
            <button type="button" className={styles.newBtn} onClick={() => setShowForm(true)}>
              + New Room
            </button>
          </div>
        ) : (
          <>
            <div className={styles.listHeader}>
              <span className={styles.roomCount}>
                {rooms.length} room{rooms.length !== 1 ? "s" : ""}
              </span>
              {atLimit ? (
                <p className={styles.limitNote}>
                  {billing?.capabilities.plan === "free"
                    ? <>Limit reached. <Link to="/premium" className={styles.limitLink}>Upgrade to Plus</Link> for 50 rooms.</>
                    : <>Limit reached. <Link to="/premium" className={styles.limitLink}>Upgrade to Pro</Link> for unlimited.</>
                  }
                </p>
              ) : (
                <button type="button" className={styles.newBtn} onClick={() => setShowForm((v) => !v)}>
                  {showForm ? "Cancel" : "+ New Room"}
                </button>
              )}
            </div>

            {showForm && (
              <form className={styles.form} onSubmit={handleCreate}>
                <h2 className={styles.formTitle}>New Room</h2>
                <label className={styles.label}>
                  Title
                  <input
                    type="text"
                    className={styles.input}
                    value={title}
                    onChange={(e) => { setTitle(e.target.value); setSlug(deriveSlug(e.target.value)); }}
                    required
                    placeholder="My Custom Room"
                  />
                </label>
                <label className={styles.label}>
                  Slug
                  <input
                    type="text"
                    className={styles.input}
                    value={slug}
                    onChange={(e) => setSlug(deriveSlug(e.target.value))}
                    required
                    placeholder="my-custom-room"
                  />
                </label>
                <label className={styles.checkLabel}>
                  <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                  Public (anyone with the link can view)
                </label>
                {error && <p className={styles.error}>{error}</p>}
                <div className={styles.formActions}>
                  <button type="submit" className={styles.submitBtn} disabled={creating}>
                    {creating ? "Creating…" : "Create Room"}
                  </button>
                </div>
              </form>
            )}

            <ul className={styles.roomList}>
            {rooms.map((room) => (
              <li key={room.id} className={styles.roomItem}>
                <div className={styles.roomIcon}>🗂️</div>
                <div className={styles.roomInfo}>
                  <span className={styles.roomTitle}>{room.title}</span>
                  <div className={styles.roomMeta}>
                    {room.sourceIds.length} source{room.sourceIds.length !== 1 ? "s" : ""}
                    <span className={`${styles.badge} ${room.isPublic ? styles.badgePublic : styles.badgePrivate}`}>
                      {room.isPublic ? "Public" : "Private"}
                    </span>
                  </div>
                </div>
                <div className={styles.roomActions}>
                  <Link
                    to="/my-rooms/edit/$roomId"
                    params={{ roomId: room.id }}
                    className={styles.editBtn}
                  >
                    Edit
                  </Link>
                  <Link
                    to="/$roomSlug"
                    params={{ roomSlug: `~${room.id}` }}
                    className={styles.viewBtn}
                  >
                    View
                  </Link>
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    onClick={() => handleDelete(room)}
                    disabled={deletingId === room.id}
                  >
                    {deletingId === room.id ? "…" : "Delete"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
          </>
        )}
      </div>
    </div>
  );
}
